import { HELP_SEARCH } from "@/constants/help";
import type { HelpSearchHighlightPart } from "@/interfaces/help";

const COMBINING_MARKS = /\p{M}/gu;
const SINGLE_QUOTES = /[‘’]/g;
const DOUBLE_QUOTES = /[“”]/g;
const DASHES = /[—–]/g;
const WHITESPACE_RUN = /\s+/;

/**
 * One character folded the way the API's normalizeHelpSearchText
 * (apps/gfgf-api/src/utils/help-search-helper.ts) folds text before matching — lowercase, accents
 * stripped, typographic quotes and dashes mapped to ASCII — so "didn't" highlights "didn’t". The
 * result can be longer or shorter than `char` ("ﬁ" → "fi", a bare combining mark → "").
 */
function foldHelpSearchChar(char: string): string {
  return char
    .toLowerCase()
    .normalize("NFKD")
    .replace(COMBINING_MARKS, "")
    .replace(SINGLE_QUOTES, "'")
    .replace(DOUBLE_QUOTES, '"')
    .replace(DASHES, "-");
}

/**
 * Splits `text` into runs that did and did not match `query`, for highlighting a search result's
 * title or summary. Case- and accent-insensitive; every occurrence of each whitespace-separated
 * query token of HELP_SEARCH.MIN_TOKEN_LENGTH+ characters is marked, overlapping or adjacent hits
 * merged into one run. Matching is plain indexOf over folded text — the query never becomes a
 * RegExp. The parts' texts always concatenate back to exactly `text`.
 */
export function splitHelpSearchHighlight(text: string, query: string): HelpSearchHighlightPart[] {
  const tokens = new Set(
    Array.from(query, foldHelpSearchChar)
      .join("")
      .split(WHITESPACE_RUN)
      .filter((token) => token.length >= HELP_SEARCH.MIN_TOKEN_LENGTH),
  );

  // Folding can change length, so each folded code unit keeps the [start, end) of the source
  // character it came from; a hit in `folded` is sliced out of `text` through these.
  let folded = "";
  const sources: Array<[number, number]> = [];
  let offset = 0;
  for (const char of text) {
    const piece = foldHelpSearchChar(char);
    for (let i = 0; i < piece.length; i++) sources.push([offset, offset + char.length]);
    folded += piece;
    offset += char.length;
  }

  const hits: Array<[number, number]> = [];
  for (const token of tokens) {
    for (let at = folded.indexOf(token); at !== -1; at = folded.indexOf(token, at + 1)) {
      const first = sources[at];
      const last = sources[at + token.length - 1];
      if (first && last) hits.push([first[0], last[1]]);
    }
  }
  hits.sort((a, b) => a[0] - b[0]);

  const parts: HelpSearchHighlightPart[] = [];
  let cursor = 0;
  for (const [start, end] of hits) {
    if (end <= cursor) continue;
    const previous = parts.at(-1);
    if (previous?.match && start <= cursor) {
      previous.text += text.slice(cursor, end);
    } else {
      if (start > cursor) parts.push({ text: text.slice(cursor, start), match: false });
      parts.push({ text: text.slice(start, end), match: true });
    }
    cursor = end;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), match: false });
  return parts;
}
